import type {
  AssignedFilter,
  CallResultRecord,
  AdminAnalyticsReport,
  CallLog,
  Insights,
  Lead,
  LeadQueueSummary,
  LeadEvent,
  LeadOutcome,
  LeadStatus,
  ConsultationStage,
  QualificationOutcome,
  QualificationOutcomeRecord,
  MorningSummary,
  IvrConfig,
  IvrOption,
  Me,
  MetaPage,
  PatientFeedbackInput,
  PatientFeedbackPreviewResult,
  PatientFeedbackSendResult,
  PublicReviewsResponse,
  Telecaller,
  WaTemplate,
  Followup,
  DashboardSnapshot,
  DashboardSnapshotType,
  SocialInboxConversation,
  SocialInboxDetailResponse,
  SocialInboxListResponse,
  SocialInboxMessage,
  SocialInboxStatus,
  WhatsAppInboxItem,
  InteractionDisposition,
  InteractionOutcome,
  ConversationAssist,
  AppointmentSlot,
} from './types';

import { API_ENDPOINTS } from './config';
import { apiFetch, API_URL } from './utils/api';
const TOKEN_KEY = 'token';
const EMAIL_KEY = 'email';

export const recordLeadRegistration = (leadId: string, payload: { center: string; reference: string; registered_on: string }) =>
  api<{ ok: boolean; already_recorded: boolean }>(`/api/leads/${encodeURIComponent(leadId)}/registration`, { method: 'POST', body: JSON.stringify(payload) });

export const fetchCrmActionsToday = () => api<{
  as_of: string;
  items: Array<{ id: string; label: string; action: string; count: number | null }>;
}>('/api/admin/actions-today');

export const fetchWatiHandoffs = () => api<{ items: Array<{
  lead_id: string; name: string | null; status: string; assigned_to: string | null; requested_at: string; next_action: string;
}> }>('/api/admin/wati-handoffs');

export const fetchCrmQualityQueue = (queue: 'attribution' | 'disposition') => api<{ action: string; items: Array<{
  lead_id: string; name: string | null; created_at: string;
}> }>(`/api/admin/quality-queue/${queue}`);

export const fetchCrmSignalExceptions = () => api<{ items: Array<{
  provider: string; delivery_id: string; event_type: string; state: string; reason: string | null; received_at: string; attempts: number;
}> }>('/api/admin/signal-exceptions');

// Token and email management (for compatibility)
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (value: string) => localStorage.setItem(TOKEN_KEY, value);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const getEmail = () => localStorage.getItem(EMAIL_KEY) ?? '';
export const setEmail = (value: string) => localStorage.setItem(EMAIL_KEY, value);

// Telecaller phone management (kept for frontend compatibility)
const PHONE_KEY = 'telecaller_phone';
export const getTelecallerPhone = () => localStorage.getItem(PHONE_KEY) ?? '';
export const setTelecallerPhone = (v: string) => localStorage.setItem(PHONE_KEY, v);

export function clearIdentity() {
  clearToken();
  localStorage.removeItem(EMAIL_KEY);
}

export class AuthError extends Error {}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = path.startsWith('http') ? path : API_URL + path;
  
  const res = await apiFetch(url, init);
  if (res.status === 401) {
    clearIdentity();
    throw new AuthError('Session expired — sign in again');
  }
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json() as Promise<T>;
}

export const fetchMe = () => api<{ user: Me }>(`${API_URL}/api/auth/me`);

export const updateMe = (patch: { name?: string; phone?: string }) =>
  api<{ user: Me }>(`${API_URL}/api/auth/me`, { method: 'POST', body: JSON.stringify(patch) });

export const createAgentKey = () =>
  api<{ success: boolean; key: string }>('/api/auth/agent-key', { method: 'POST' });

export const revokeAgentKey = () =>
  api<{ success: boolean }>('/api/auth/agent-key', { method: 'DELETE' });

export const fetchTelecallers = () => api<{ telecallers: Telecaller[] }>(API_ENDPOINTS.TELECALLERS);
export const fetchTelecallerProfile = () => api<{ me: Telecaller }>('/api/me');
export const updateTelecallerCenter = (center: NonNullable<Telecaller['center']>) =>
  api<{ me: Telecaller }>('/api/me', { method: 'POST', body: JSON.stringify({ center }) });
export const fetchAssignmentNotifications = (cursor: { createdAt: string; id?: string } | string) => {
  const createdAt = typeof cursor === 'string' ? cursor : cursor.createdAt;
  const id = typeof cursor === 'string' ? '' : cursor.id || '';
  const query = `since=${encodeURIComponent(createdAt)}${id ? `&since_id=${encodeURIComponent(id)}` : ''}`;
  return api<{ notifications: Array<{ id?: string; lead_id: string; lead_name: string; center: string | null; created_at: string }> }>(
    `/api/notifications/assignments?${query}`
  );
};

export const fetchLeadSection = (
  view: 'queue' | 'unassigned' | 'archived',
  params: {
    limit?: number;
    offset?: number;
    source?: string[];
    statuses?: string[];
    tier?: 1 | 2 | 3;
    createdFrom?: string;
    createdTo?: string;
    reasonGroup?: string;
    reasonCategory?: string;
    reasonCode?: string;
    reasonText?: string;
  } = {}
) => {
  const qs = new URLSearchParams({ view });
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.source?.length) qs.set('source', params.source.join(','));
  if (params.statuses?.length) qs.set('statuses', params.statuses.join(','));
  if (params.tier) qs.set('tier', String(params.tier));
  if (params.createdFrom) qs.set('createdFrom', params.createdFrom);
  if (params.createdTo) qs.set('createdTo', params.createdTo);
  if (params.reasonGroup) qs.set('reasonGroup', params.reasonGroup);
  if (params.reasonCategory) qs.set('reasonCategory', params.reasonCategory);
  if (params.reasonCode) qs.set('reasonCode', params.reasonCode);
  if (params.reasonText) qs.set('reasonText', params.reasonText);
  return api<{ leads: Lead[]; has_more?: boolean; total?: number; summary?: LeadQueueSummary; raw_rings?: number; archived?: number }>(
    `${API_ENDPOINTS.LEADS.LIST}?${qs.toString()}`
  );
};

export const fetchQueue = (assigned: AssignedFilter = '') => {
  if (assigned === 'unassigned') {
    return fetchLeadSection('unassigned', { limit: 50 });
  }
  return fetchLeadSection('queue');
};

export const fetchAllLeads = (params: {
  status?: string;
  statuses?: string[];
  tier?: string;
  source?: string;
  campaigns?: string[];
  leadStatuses?: string[];
  q?: string;
  name?: string;
  phone?: string;
  email?: string;
  leadId?: string;
  assigned?: string;
  clinic?: string;
  tags?: string;
  createdFrom?: string;
  createdTo?: string;
  assignmentFrom?: string;
  assignmentTo?: string;
  dispositionFrom?: string;
  dispositionTo?: string;
  reasonGroup?: string;
  reasonCategory?: string;
  reasonCode?: string;
  reasonText?: string;
  followup?: string;
  followupFrom?: string;
  followupTo?: string;
  includeClosed?: boolean;
  limit?: number;
  offset?: number;
}, signal?: AbortSignal) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      if (Array.isArray(v)) {
        if (v.length) qs.set(k, v.join(','));
      } else if (typeof v === 'boolean') {
        qs.set(k, v ? 'true' : 'false');
      } else {
        qs.set(k, String(v));
      }
    }
  }
  return api<{ leads: Lead[]; has_more?: boolean }>(API_ENDPOINTS.LEADS.ALL + '?' + qs, { signal });
};

function paramsToSearch(params: Record<string, unknown>) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      if (Array.isArray(v)) {
        if (v.length) qs.set(k, v.join(','));
      } else if (typeof v === 'boolean') {
        qs.set(k, v ? 'true' : 'false');
      } else {
        qs.set(k, String(v));
      }
    }
  }
  return qs;
}

async function downloadCsv(path: string, filename: string) {
  const response = await apiFetch(`${API_URL}${path}`, {
    headers: { Accept: 'text/csv' },
  });
  if (!response.ok) {
    throw new Error(`${response.status} ${await response.text()}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const exportLeadsCsv = (params: Parameters<typeof fetchAllLeads>[0]) => {
  const qs = paramsToSearch(params);
  return downloadCsv(`/api/leads/export?${qs.toString()}`, 'crmai-leads.csv');
};

export const claimLead = (id: string) =>
  api<{ ok: true; assigned_to: string }>(`${API_URL}/api/leads/${id}/claim`, {
    method: 'POST',
  });

export const fetchLead = (id: string) =>
  api<{
    lead: Lead;
    events: LeadEvent[];
    calls: CallLog[];
    outcomes?: LeadOutcome[];
    qualification_outcome?: QualificationOutcomeRecord | null;
    attribution?: import('./types').LeadAttribution | null;
    reported_source?: import('./types').LeadReportedSource | null;
    payment?: {
      amount_minor: number;
      currency: string;
      provider_payment_id: string | null;
      verified_at: string | null;
      location: string | null;
    } | null;
  }>(
    API_ENDPOINTS.LEADS.DETAIL(id)
  );

export const fetchAppointmentAvailability = (center: string, date: string) =>
  api<{ center: string; service: 'FIRST_CONSULT'; date: string; slots: AppointmentSlot[] }>(
    `/api/appointments/availability?center=${encodeURIComponent(center)}&date=${encodeURIComponent(date)}`,
  );

export const updateReportedSource = (
  id: string,
  data: { category: import('./types').LeadReportedSource['category']; detail?: string },
) => api<{ reported_source: import('./types').LeadReportedSource }>(
  `${API_URL}/api/leads/${id}/reported-source`,
  { method: 'POST', body: JSON.stringify(data) },
);

export const createManualLead = (data: { name: string; phone: string; source?: string; notes?: string }) =>
  api<{ ok: true; lead_id: string }>(`${API_URL}/api/leads/create`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateStatus = (
  id: string,
  status: LeadStatus,
  opts: {
    note?: string;
    reason_code?: string;
    reason_text?: string;
    reason_group?: string;
    reason_category?: string;
    qualification_outcome?: QualificationOutcome;
    qualification_outcome_reason?: string;
    next_action_date?: string;
    next_action_time?: string;
    schedule_consultation?: boolean;
    consultation_date?: string;
    consultation_time?: string;
    consultation_mode?: 'Clinic' | 'Phone' | 'Video';
    consultation_location?: string;
    center?: NonNullable<Telecaller['center']>;
  } = {}
) =>
  api<{ ok: true; from: string; to: string; reason_code: string | null; reason_text: string | null }>(
    API_ENDPOINTS.LEADS.UPDATE_STATUS(id),
    {
      method: 'POST',
      body: JSON.stringify({ status, ...opts }),
    }
  );

export const recordLeadMilestone = (
  id: string,
  milestone: string,
  opts: { note?: string; value_amount?: number | null } = {}
) =>
  api<{ ok: true; milestone: string; outcomes: LeadOutcome[] }>(`${API_URL}/api/leads/${id}/milestone`, {
    method: 'POST',
    body: JSON.stringify({ milestone, ...opts }),
  });

export const updateConsultationOutcome = (
  id: string,
  stage: ConsultationStage,
  opts: { consultation_date?: string; consultation_time?: string } = {}
) =>
  api<{ ok: true; stage: ConsultationStage; lead: Lead }>(`${API_URL}/api/leads/${id}/consultation-outcome`, {
    method: 'POST',
    body: JSON.stringify({ stage, ...opts }),
  });

export const confirmConsultation = (
  id: string,
  data: {
    consultation_date?: string;
    consultation_time?: string;
    consultation_mode?: 'Clinic' | 'Phone' | 'Video';
    consultation_location?: string;
    confirmation_source: 'Practo' | 'Clinic register' | 'CRM manual';
    confirmation_reference: string;
  },
) =>
  api<{ ok: true; already_confirmed: boolean; consultation_status: 'confirmed'; lead: Lead | null }>(
    API_ENDPOINTS.LEADS.CONFIRM_CONSULTATION(id),
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );

export const updateTier = (id: string, tier: 1 | 2 | 3) =>
  api<{ ok: true; from: number; to: number }>(`${API_URL}/api/leads/${id}/tier`, {
    method: 'POST',
    body: JSON.stringify({ tier }),
  });

export const updateLeadName = (id: string, name: string) =>
  api<{ ok: true; name: string }>(`${API_URL}/api/leads/${id}/name`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const assignLead = (id: string, assigned_to: string | null) =>
  api<{ ok: true; assigned_to: string | null }>(API_ENDPOINTS.LEADS.ASSIGN(id), {
    method: 'POST',
    body: JSON.stringify({ assigned_to }),
  });

export const addLeadNote = (id: string, note: string) =>
  api<{ ok: true }>(`${API_URL}/api/leads/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });

export const initCall = (lead_id: string, telecaller_phone: string) =>
  api<{ ok: true }>(API_ENDPOINTS.ACTIONS.CALL, {
    method: 'POST',
    body: JSON.stringify({ lead_id, telecaller_phone }),
  });

export const sendWhatsapp = (
  lead_id: string,
  payload: {
    text?: string;
    template_name?: string;
    params?: string[];
    media?: { type: 'image' | 'video' | 'document'; url: string };
    client_temp_id?: string;
  }
) =>
  api<{
    ok: true;
    provider?: string;
    status?: string;
    provider_message_id?: string | null;
    raw?: string;
    message?: import('./types').WhatsAppMessageRecord;
    disposition?: InteractionDisposition | null;
  }>(API_ENDPOINTS.ACTIONS.WHATSAPP, {
    method: 'POST',
    body: JSON.stringify({ lead_id, ...payload }),
  });

export const sendWhatsAppFile = (lead_id: string, file: File, caption = '') => {
  const body = new FormData();
  body.append('lead_id', lead_id);
  body.append('file', file, file.name);
  if (caption.trim()) body.append('text', caption.trim());
  return api<{
    ok: true;
    provider: string;
    status: string;
    provider_message_id: string | null;
    message?: import('./types').WhatsAppMessageRecord;
    disposition?: InteractionDisposition | null;
  }>(API_ENDPOINTS.ACTIONS.WHATSAPP, { method: 'POST', body });
};

export const fetchWhatsAppMessages = (id: string, pageNumber = 1, pageSize = 100) => {
  const query = new URLSearchParams({ page_number: String(pageNumber), page_size: String(pageSize) });
  return api<{ provider: string; messages: import('./types').WhatsAppMessageRecord[] }>(
    `${API_ENDPOINTS.LEADS.WHATSAPP_MESSAGES(id)}?${query}`,
  );
};

export const fetchConversationReplySuggestion = (leadId: string) =>
  api<{ suggestion: ConversationAssist }>(`/api/leads/${encodeURIComponent(leadId)}/assistant/reply`, { method: 'POST' });

export const fetchWhatsAppInbox = (view: 'queue' | 'unassigned' = 'queue') =>
  api<{ pending_count: number; items: WhatsAppInboxItem[] }>(
    `${API_ENDPOINTS.WHATSAPP_INBOX}?view=${view}`,
  );

export const markWhatsAppRead = (id: string) =>
  api<{ ok: true }>(API_ENDPOINTS.LEADS.WHATSAPP_READ(id), {
    method: 'POST',
  });

export const fetchSocialInbox = (params: { status?: string; platform?: string; type?: string; search?: string; cursor?: string; limit?: number } = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.platform) qs.set('platform', params.platform);
  if (params.type) qs.set('type', params.type);
  if (params.search) qs.set('search', params.search);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<SocialInboxListResponse>(`${API_ENDPOINTS.SOCIAL.INBOX}${suffix}`);
};

export const fetchSocialInboxConversation = (id: string) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.DETAIL(id));

export const markSocialInboxRead = (id: string) =>
  api<{ conversation: SocialInboxConversation; messages: SocialInboxMessage[] }>(API_ENDPOINTS.SOCIAL.READ(id), {
    method: 'POST',
  });

export const updateSocialInboxStatus = (id: string, status: SocialInboxStatus, assignee?: string | null) =>
  api<{ conversation: SocialInboxConversation; messages: SocialInboxMessage[] }>(API_ENDPOINTS.SOCIAL.STATUS(id), {
    method: 'POST',
    body: JSON.stringify({ status, assignee }),
  });

export const assignSocialInbox = (id: string, assignee?: string | null) =>
  api<{ conversation: SocialInboxConversation; messages: SocialInboxMessage[] }>(API_ENDPOINTS.SOCIAL.ASSIGN(id), {
    method: 'POST',
    body: JSON.stringify({ assignee }),
  });

export const replySocialInbox = (id: string, text: string, expectedLastMessageAt?: string | null) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.REPLY(id), {
    method: 'POST',
    body: JSON.stringify({ text, expected_last_message_at: expectedLastMessageAt }),
  });

export const suggestSocialReply = (id: string) =>
  api<{ suggestion: { suggested_reply: string; next_action: string; summary: string } }>(API_ENDPOINTS.SOCIAL.ASSISTANT(id), { method: 'POST' });

export const privateReplySocialInbox = (id: string, text: string, expectedLastMessageAt?: string | null) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.PRIVATE_REPLY(id), {
    method: 'POST',
    body: JSON.stringify({ text, expected_last_message_at: expectedLastMessageAt }),
  });

export const fetchPendingDispositions = (params: { leadId?: string; conversationId?: string; mine?: boolean } = {}) => {
  const query = new URLSearchParams();
  if (params.leadId) query.set('lead_id', params.leadId);
  if (params.conversationId) query.set('conversation_id', params.conversationId);
  if (params.mine === false) query.set('mine', 'false');
  return api<{ items: InteractionDisposition[] }>(`/api/dispositions/pending?${query.toString()}`);
};

export const fetchGbpStatus = () => api<{
  connection: { provider: 'markifact' | 'google_direct'; ready: boolean; enabled: boolean; oauth_configured: boolean; provider_configured: boolean; markifact_connection_id: string | null; locations_configured: number; locations: Array<{ name: string; whatsapp_tracked: boolean; website_tracked: boolean; vobiz_number_mapped: boolean }> };
  last_sync: { status?: string; completed_at?: string } | null;
  metrics_7d: Array<{ metric: string; value: number }>;
  reviews: { total: number; needs_reply: number };
}>('/api/admin/gbp/status');

export const completeInteractionDisposition = (id: string, payload: { outcome: InteractionOutcome; reason_code?: string | null; follow_up_at?: string | null }) =>
  api<{ item: InteractionDisposition }>(`/api/dispositions/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const likeSocialInbox = (id: string) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.LIKE(id), { method: 'POST' });

export const unlikeSocialInbox = (id: string) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.UNLIKE(id), { method: 'POST' });

export const hideSocialInbox = (id: string) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.HIDE(id), { method: 'POST' });

export const unhideSocialInbox = (id: string) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.UNHIDE(id), { method: 'POST' });

export const deleteSocialInboxComment = (id: string) =>
  api<SocialInboxDetailResponse>(API_ENDPOINTS.SOCIAL.DELETE(id), { method: 'POST' });

export const linkSocialInboxLead = (id: string, lead_id: string) =>
  api<{ conversation: SocialInboxConversation; messages: SocialInboxMessage[] }>(API_ENDPOINTS.SOCIAL.LEAD(id), {
    method: 'POST',
    body: JSON.stringify({ lead_id }),
  });

export const fetchInsights = (scope: 'team' | 'me' = 'team') =>
  api<Insights>(API_ENDPOINTS.INSIGHTS(scope));

export const fetchWhatsAppTeamInsights = () =>
  api<{ unique_replied_today: number; unread_whatsapp: number }>(API_ENDPOINTS.WHATSAPP_TEAM_INSIGHTS);

export const fetchLatestDashboardSnapshots = () =>
  api<{ snapshots: DashboardSnapshot[] }>(API_ENDPOINTS.ADMIN.DASHBOARD_SNAPSHOTS_LATEST);

export const fetchDashboardSnapshots = (date?: string) =>
  api<{ snapshots: DashboardSnapshot[] }>(`${API_ENDPOINTS.ADMIN.DASHBOARD_SNAPSHOTS}${date ? `?date=${encodeURIComponent(date)}` : ''}`);

export const generateDashboardSnapshot = (snapshot_type: DashboardSnapshotType) =>
  api<{ snapshot: DashboardSnapshot }>(API_ENDPOINTS.ADMIN.DASHBOARD_SNAPSHOTS_GENERATE, {
    method: 'POST',
    body: JSON.stringify({ snapshot_type }),
  });

export const fetchQualityAffectedLeads = (metric: string, page = 1, limit = 20) =>
  api<{ leads: Array<{ lead_id: string; phone_identity: string; source: string; status: string; assigned_agent_id: string | null }>; total: number; page: number; limit: number }>(`${API_ENDPOINTS.ADMIN.QUALITY_CONTROL_AFFECTED_LEADS}?metric=${encodeURIComponent(metric)}&page=${page}&limit=${limit}`);

export const initializeQualityControl = () =>
  api<{ processed: number; failed: number; snapshots_upgraded: number }>(API_ENDPOINTS.ADMIN.QUALITY_CONTROL_INITIALIZE, { method: 'POST' });

export const fetchMorningSummary = (scope: 'team' | 'me' = 'team') =>
  api<MorningSummary>(API_ENDPOINTS.MORNING_SUMMARY(scope));

export const regenerateMorningSummary = (scope: 'team' | 'me' = 'team') =>
  api<MorningSummary>(API_ENDPOINTS.MORNING_SUMMARY_REGENERATE, {
    method: 'POST',
    body: JSON.stringify({ scope }),
  });

export const fetchAdminAnalyticsLatest = (type: 'eod' | 'morning') =>
  api<{ report: AdminAnalyticsReport | null; stale: boolean }>(
    type === 'eod' ? API_ENDPOINTS.ADMIN.ANALYTICS_EOD_LATEST : API_ENDPOINTS.ADMIN.ANALYTICS_MORNING_LATEST
  );

export const generateAdminAnalytics = (type: 'eod' | 'morning') =>
  api<{ ok: boolean; report: AdminAnalyticsReport | null; message?: string; previous_completed_report?: AdminAnalyticsReport | null }>(
    type === 'eod' ? API_ENDPOINTS.ADMIN.ANALYTICS_EOD_GENERATE : API_ENDPOINTS.ADMIN.ANALYTICS_MORNING_GENERATE,
    { method: 'POST' }
  );

export const regenerateAdminAnalytics = (type: 'eod' | 'morning') =>
  api<{ ok: boolean; report: AdminAnalyticsReport | null; previous_completed_report?: AdminAnalyticsReport | null }>(
    type === 'eod' ? API_ENDPOINTS.ADMIN.ANALYTICS_EOD_REGENERATE : API_ENDPOINTS.ADMIN.ANALYTICS_MORNING_REGENERATE,
    { method: 'POST' }
  );

export async function downloadAdminAnalyticsExcel(report: AdminAnalyticsReport) {
  const response = await apiFetch(API_ENDPOINTS.ADMIN.ANALYTICS_REPORT_EXPORT(report.id), {
    headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  });
  if (!response.ok) throw new Error(`${response.status} ${await response.text()}`);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const date = report.window_end.slice(0, 10);
  link.download = report.report_type === 'eod' ? `CRMAI_EOD_Report_${date}.xlsx` : `CRMAI_Morning_Report_${date}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const fetchWaTemplates = () =>
  api<{ templates: WaTemplate[] }>(API_ENDPOINTS.WA_TEMPLATES);

// ── Admin: Meta Pages ──
export const fetchMetaPages = () =>
  api<{ pages: MetaPage[] }>(API_ENDPOINTS.ADMIN.META_PAGES);

export const upsertMetaPage = (page_id: string, access_token: string, name?: string) =>
  api<{ ok: true; page_id: string; name: string | null }>(API_ENDPOINTS.ADMIN.META_PAGES, {
    method: 'POST',
    body: JSON.stringify({ page_id, access_token, name }),
  });

export const toggleMetaPage = (page_id: string, active: boolean) =>
  api<{ ok: true; active: boolean }>(API_ENDPOINTS.ADMIN.META_PAGES_TOGGLE(page_id), {
    method: 'POST',
    body: JSON.stringify({ active }),
  });

export const deleteMetaPage = (page_id: string) =>
  api<{ ok: true }>(API_ENDPOINTS.ADMIN.META_PAGES_DELETE(page_id), {
    method: 'DELETE',
  });

export const previewPatientFeedbackUpload = (patients: PatientFeedbackInput[]) =>
  api<PatientFeedbackPreviewResult>(API_ENDPOINTS.ADMIN.PATIENT_FEEDBACK_PREVIEW, {
    method: 'POST',
    body: JSON.stringify({ patients }),
  });

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

async function sendPatientFeedbackBatch(patients: PatientFeedbackInput[], force = false) {
  return api<PatientFeedbackSendResult>(API_ENDPOINTS.ADMIN.PATIENT_FEEDBACK_SEND, {
    method: 'POST',
    body: JSON.stringify({ patients, force }),
  });
}

export const sendPatientFeedback = async (patients: PatientFeedbackInput[], force = false) => {
  if (!patients.length) {
    return {
      success: true,
      total: 0,
      sent: 0,
      failed: 0,
      duplicate: 0,
      results: [],
      batchId: '',
    };
  }

  const batches = chunkArray(patients, 20);
  const batchResults = await batches.reduce<Promise<PatientFeedbackSendResult[]>>(
    (pending, batch) =>
      pending.then(async (completed) => {
        completed.push(await sendPatientFeedbackBatch(batch, force));
        return completed;
      }),
    Promise.resolve([])
  );
  const results: PatientFeedbackSendResult['results'] = [];
  let sent = 0;
  let failed = 0;
  let duplicate = 0;
  let batchId = '';

  for (const result of batchResults) {
    batchId = batchId || result.batchId;
    sent += result.sent;
    failed += result.failed;
    duplicate += result.duplicate;
    results.push(...result.results);
  }

  return {
    success: true,
    total: results.length,
    sent,
    failed,
    duplicate,
    results,
    batchId,
  };
};

export const fetchPublicReviews = () =>
  api<PublicReviewsResponse>(API_ENDPOINTS.ADMIN.REVIEWS);

export const importPublicReviewsText = (sourceText: string, locationName?: string) =>
  api<{ ok: boolean; parsed: number; imported: number; updated: number }>(
    API_ENDPOINTS.ADMIN.REVIEWS_IMPORT_TEXT,
    {
      method: 'POST',
      body: JSON.stringify({ sourceText, locationName }),
    }
  );

export const syncPublicReviewsToMos = () =>
  api<{ ok: boolean; total?: number; skipped?: boolean; reason?: string; body?: unknown }>(
    API_ENDPOINTS.ADMIN.REVIEWS_SYNC_MOS,
    { method: 'POST' }
  );

export const markPublicReviewReplied = (id: string, replyText: string) =>
  api<{ ok: true }>(API_ENDPOINTS.ADMIN.REVIEWS_MARK_REPLIED(id), {
    method: 'POST',
    body: JSON.stringify({ replyText }),
  });

export const updatePublicReviewRecovery = (
  id: string,
  status: 'not_needed' | 'needs_review' | 'in_progress' | 'resolved',
  note?: string
) =>
  api<{ ok: true }>(API_ENDPOINTS.ADMIN.REVIEWS_RECOVERY(id), {
    method: 'POST',
    body: JSON.stringify({ status, note }),
  });

// ── Admin: IVR configs ──
export const fetchIvrConfigs = () =>
  api<{ configs: IvrConfig[] }>(API_ENDPOINTS.ADMIN.IVR_CONFIGS);

export const upsertIvrConfig = (cfg: {
  campaign: string;
  display_name?: string | null;
  greeting: string;
  greeting_audio_url?: string | null;
  thank_you_audio_url?: string | null;
  options: IvrOption[];
  no_input_tier: number;
  no_input_topic: string;
  no_input_tip?: string | null;
  no_input_opening?: string | null;
  voice: 'woman' | 'man';
  active: boolean;
  business_hours_start?: string;
  business_hours_end?: string;
  business_days?: string;
  timezone?: string;
  after_hours_greeting?: string | null;
  after_hours_greeting_audio_url?: string | null;
  after_hours_options?: IvrOption[];
  support_phone?: string | null;
}) =>
  api<{ ok: true; campaign: string }>(API_ENDPOINTS.ADMIN.IVR_CONFIGS, {
    method: 'POST',
    body: JSON.stringify(cfg),
  });

export const deleteIvrConfig = (campaign: string) =>
  api<{ ok: true }>(API_ENDPOINTS.ADMIN.IVR_CONFIGS_DELETE(campaign), {
    method: 'DELETE',
  });

// ── Follow-ups ──
export const fetchCallResult = (leadId: string) =>
  api<{ call: CallResultRecord | null }>(`${API_URL}/api/leads/${leadId}/call-result`);

export const fetchLeadFollowups = (leadId: string) =>
  api<{ followups: Followup[] }>(`${API_URL}/api/leads/${leadId}/followups`);

export const fetchPendingFollowups = () =>
  api<{ followups: Followup[] }>(`${API_URL}/api/followups/pending`);

export const createFollowup = (leadId: string, data: {
  followup_date: string;
  followup_time?: string;
  notes?: string;
}) =>
  api<{ ok: true; id: string }>(`${API_URL}/api/leads/${leadId}/followups`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateFollowupStatus = (followupId: string, status: 'completed' | 'cancelled') =>
  api<{ ok: true; status: string }>(`${API_URL}/api/followups/${followupId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });

// ── Call Logs ──
export const fetchCallLogs = (params: {
  type?: 'all' | 'outgoing' | 'incoming' | 'missed';
  date?: 'today' | 'yesterday' | 'last7' | 'last30' | 'range';
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  search?: string;
  campaign?: string;
}, signal?: AbortSignal) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      qs.set(k, String(v));
    }
  }
  return api<{ call_logs: any[]; has_more: boolean; page: number; limit: number }>(
    `${API_URL}/api/call-logs?${qs.toString()}`,
    { signal }
  );
};

export const fetchMyRecentIvrCalls = (cursor?: { createdAt: string; id?: string }) => {
  const query = cursor
    ? `?since=${encodeURIComponent(cursor.createdAt)}${cursor.id ? `&since_id=${encodeURIComponent(cursor.id)}` : ''}`
    : '';
  return api<{ delta?: boolean; calls: Array<{
    id?: string;
    call_uuid: string | null;
    lead_id: string | null;
    customer_phone: string;
    patient_name: string | null;
    lead_phone: string | null;
    call_status: string;
    status: string;
    duration_seconds: number;
    created_at: string;
    finalized_at: string | null;
    agent_id: string | null;
    agent_email: string | null;
    needs_disposition: boolean;
  }> }>(`/api/call-logs/my-recent-ivr${query}`);
};

export const exportCallLogsCsv = (params: Parameters<typeof fetchCallLogs>[0]) => {
  const qs = paramsToSearch({ ...params, format: 'csv' });
  return downloadCsv(`/api/call-logs?${qs.toString()}`, 'crmai-call-logs.csv');
};

export const fetchSipCredentials = () =>
  api<{ endpoint: string; username: string; password: string }>('/api/agent/sip-credentials');


export type WaitingItem = {
  id: string; name: string | null; source: string; created_at: string; status: string; tier_score: number | null;
  assigned_to_me: boolean; unassigned: boolean; opening_line: string; tip: string | null;
  waiting_since: string | null; patient_choice: string | null;
};
export type WaitingList = {
  as_of: string; waiting_total: number; waiting_mine: number; waiting_unclaimed: number; older_in_my_queue: number;
  buckets: Array<{ key: string; label: string; total: number; hidden: number; items: WaitingItem[] }>;
};
export const fetchWaitingList = () => api<WaitingList>(`${API_URL}/api/work/waiting`);
export type MyDay = { day: string; calls: number; reached: number; results: number; positives: number; follow_ups_due: number };
export const fetchMyDay = () => api<MyDay>(`${API_URL}/api/work/my-day`);

export const restoreArchivedLead = (leadId: string) =>
  api<{ ok: boolean }>(`${API_URL}/api/leads/${encodeURIComponent(leadId)}/restore-archive`, { method: 'POST' });

// JEV Assist (counsellor teleprompter)
type JevClaim = { id: string; text: string; source: string };
export type JevPayload = {
  assessment_id: string | null; extract_enabled?: boolean; formula_version: string; content_version: string; pending_signoff: boolean; offer_arm: 'A' | 'B';
  strip: { light: 'red' | 'amber' | 'green'; completeness: number; total: number; stage: string; persona: string | null; worry: string | null; doctor_first: boolean;
    ask: Array<{ key: string; question: string; options: string[]; hint: string | null }>;
    hints: Array<{ key: string; value: string; source: string; confidence: number | null }>;
    known?: Array<{ key: string; value: string; question: string; options: string[] }> };
  cards: Record<string, { empathy: string; claim: JevClaim | null; next: string }>;
  universal: { opener: string; anti_push: string; dont_know: string; medical?: string };
  red_flags?: Array<{ key: string; label: string; action: string }>;
  doctor_questions?: Array<{ text: string; by: string; at: string }>;
  prompter: null | { opener: string; evidence: JevClaim; worry: null | { empathy: string; claim: JevClaim | null; next: string };
    pathway: string; anti_push: string | null; first_step: string; options: Array<{ n: number; kind: string; line: string }>; ask: string };
};
export const fetchJev = (leadId: string) => api<JevPayload>(`${API_URL}/api/leads/${encodeURIComponent(leadId)}/jev`);
export const confirmJevFact = (leadId: string, key: string, value: string) =>
  api<JevPayload>(`${API_URL}/api/leads/${encodeURIComponent(leadId)}/jev/facts`, { method: 'POST', body: JSON.stringify({ key, value }) });
export const readJevChat = (leadId: string) =>
  api<JevPayload>(`${API_URL}/api/leads/${encodeURIComponent(leadId)}/jev/extract`, { method: 'POST' });
export const logJevEvent = (leadId: string, kind: 'offer' | 'objection' | 'red_flag' | 'doctor_question', value: string, assessmentId: string | null) =>
  api<{ ok: boolean }>(`${API_URL}/api/leads/${encodeURIComponent(leadId)}/jev/event`, { method: 'POST', body: JSON.stringify({ kind, value, assessment_id: assessmentId }) });
